import type { Language, LanguageConfig, Direction } from "@/types/theme";
import { landingPageSupportedLanguages } from "@/i18n/landingPage";

/**
 * Builds visible languages configuration from available language codes and visibility settings
 * @param availableLanguages - Array of language codes that are available in the data
 * @param visibleLanguages - Array of language codes that should be visible in the selector
 * @returns Array of LanguageConfig objects for visible languages
 */
export function buildVisibleLanguages(
  availableLanguages: string[],
  visibleLanguages: Language[]
): LanguageConfig[] {
  return visibleLanguages
    .filter((langCode) => availableLanguages.includes(langCode))
    .map((langCode) => {
      const langConfig = landingPageSupportedLanguages.find(
        (lang) => lang.code === langCode
      );

      // Return the predefined config or a fallback
      return (
        langConfig || {
          code: langCode,
          name: langCode.toUpperCase(),
          nativeName: langCode.toUpperCase(),
          direction: "ltr" as Direction,
          flag: "🏳️",
        }
      );
    });
}

/**
 * Legacy support - builds supported languages configuration from available language codes
 * @deprecated Use buildVisibleLanguages instead
 */
export function buildSupportedLanguages(
  availableLanguages: string[],
  headerSupportedLanguages?: LanguageConfig[]
): LanguageConfig[] {
  return availableLanguages.map((langCode: string) => {
    // First check if we have it in the header's supportedLanguages
    const headerLang = headerSupportedLanguages?.find(
      (lang: LanguageConfig) => lang.code === langCode
    );

    // If found in header, use it; otherwise use default config
    return (
      headerLang ||
      landingPageSupportedLanguages.find(lang => lang.code === langCode) || {
        code: langCode as Language,
        name: langCode.toUpperCase(),
        nativeName: langCode.toUpperCase(),
        direction: "ltr" as Direction,
        flag: "🏳️",
      }
    );
  });
}
